import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Package, DollarSign, Truck, Inbox } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { fmtBRL } from "@/lib/format";

const STATUS_LABEL = {
  aguardando_confirmacao: "Aguardando confirmação",
  confirmado: "Confirmado",
  saiu_para_entrega: "Saiu para entrega",
  concluido: "Concluído",
  cancelado: "Cancelado",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div className="animate-pulse space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 bg-white rounded-xl border border-slate-200" />
          ))}
        </div>
        <div className="h-72 bg-white rounded-xl border border-slate-200" />
      </div>
    );
  }

  const cards = [
    { icon: ClipboardList, label: "Pedidos", value: stats.total_orders, sub: `${stats.orders_by_status.aguardando_confirmacao || 0} aguardando confirmação`, testid: "stat-orders" },
    { icon: Truck, label: "Entregas agendadas", value: stats.scheduled_deliveries ?? 0, sub: "próximas entregas", testid: "stat-scheduled" },
    { icon: DollarSign, label: "Valor em pedidos", value: fmtBRL(stats.revenue), sub: "exceto cancelados", testid: "stat-revenue" },
    { icon: Package, label: "Produtos ativos", value: `${stats.active_products}/${stats.total_products}`, sub: `${stats.low_stock} com estoque baixo`, testid: "stat-products" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading text-2xl font-bold text-gray-900">Dashboard</h1>
      <p className="text-sm text-gray-500 mt-1">Visão geral da sua loja</p>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({ icon: Icon, label, value, sub, testid }) => (
          <div key={label} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
            <div className="flex items-center gap-2 text-slate-500 text-xs font-medium uppercase tracking-wide">
              <Icon className="w-4 h-4 text-brand" /> {label}
            </div>
            <p className="mt-2 font-heading text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="orders-chart">
          <h2 className="font-heading font-semibold text-gray-900">Pedidos — últimos 14 dias</h2>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.orders_per_day}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={1} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={24} />
                <Tooltip />
                <Bar dataKey="orders" name="Pedidos" fill="#0B5394" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="recent-orders">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-gray-900">Pedidos recentes</h2>
            <Link to="/admin/pedidos" className="text-sm text-brand font-medium hover:underline" data-testid="see-all-orders">
              Ver todos
            </Link>
          </div>
          {stats.recent_orders.length === 0 ? (
            <div className="mt-8 text-center text-sm text-slate-400">
              <Inbox className="w-10 h-10 mx-auto text-slate-200" />
              <p className="mt-2">Nenhum pedido ainda. Os pedidos da loja aparecem aqui.</p>
            </div>
          ) : (
            <ul className="mt-4 divide-y divide-slate-100">
              {stats.recent_orders.map((o) => (
                <li key={o.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800 truncate">{o.customer.name}</p>
                    <p className="text-xs text-slate-400">
                      #{o.id} • {o.customer.city}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold text-gray-900">{fmtBRL(o.total)}</p>
                    <p className="text-xs text-slate-400">{STATUS_LABEL[o.status] || o.status}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
